import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import cx from 'classnames';

import Icon from 'components/Icon';
import { BodyOverflowContext } from 'contexts/BodyOverflowContext';

import styles from './styles.module.scss';

const Modal = ({
	title,
	modifiers,
	background,
	modifierClass,
	onAfterClose,
	size,
	theme,
	backButton,
	onRequestClose,
	isOpen,
	autoClose,
	children,
	shouldCloseOnOverlayClick
}) => {
	const contentRef = useRef(null);
	const [, setBodyOverflowIsHidden] = useContext(BodyOverflowContext);
	const [timeoutHandle, setTimeoutHandle] = useState(null);

	const resetTimeout = useCallback(() => {
		if (timeoutHandle) {
			clearTimeout(timeoutHandle);
			setTimeoutHandle(null);
		}
	}, [timeoutHandle]);

	useEffect(() => {
		setBodyOverflowIsHidden('modal', isOpen);

		return () => {
			setBodyOverflowIsHidden('modal', false);
			resetTimeout();
		};
	}, [isOpen]);

	const onRequestCloseModal = useCallback(() => {
		resetTimeout();

		onRequestClose?.();
	}, [onRequestClose, resetTimeout]);

	const onAfterOpen = useCallback(() => {
		if (autoClose && onRequestClose) {
			setTimeoutHandle(setTimeout(onRequestClose, autoClose));
		}
	}, [onRequestClose, autoClose]);

	const modalContentStyles = background
		? { backgroundImage: `url("${background}")` }
		: {};

	const modalContentCx = cx(
		styles.modalContent,
		styles[`theme_${theme}`],
		styles[`size_${size}`]
	);

	useEffect(() => {
		if (contentRef.current) {
			contentRef.current.focus();
		}
	}, []);

	return (
		<ReactModal
			modifiers={modifiers}
			isOpen={isOpen}
			onRequestClose={onRequestCloseModal}
			shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
			onAfterOpen={onAfterOpen}
			appElement={document.body}
			closeTimeoutMS={200}
			className={cx(styles.content, styles[modifierClass])}
			onAfterClose={onAfterClose}
			overlayClassName={styles.modal}
			aria={{}}
			style={{
				overlay: {
					background: 'rgba(0, 0, 0, 0.8)',
					backdropFilter: 'blur(8px)'
				}
			}}
		>
			{modifierClass !== 'minimalism' && (
				<button
					className={styles.close}
					type="button"
					onClick={onRequestClose}
					title="Закрыть"
					aria-label="Закрыть модальное окно"
				>
					<Icon icon="close" inheritSize={false} />
				</button>
			)}
			<div
				className={modalContentCx}
				style={modalContentStyles}
				ref={contentRef}
			>
				{(title || backButton) && (
					<div className={styles.head}>
						{title ? (
							<h3 className={styles.title}>{title}</h3>
						) : (
							<button
								className={styles.title}
								type="button"
								onClick={backButton.onClick}
							>
								<Icon icon="chevronStrokeLeft" className={styles.titleIcon} />
								{backButton.title}
							</button>
						)}
					</div>
				)}
				<div className={styles.body}>{children}</div>
				<div className={styles.footer} />
			</div>
		</ReactModal>
	);
};

Modal.propTypes = {
	modifiers: PropTypes.arrayOf(PropTypes.string),
	isOpen: PropTypes.bool,
	onRequestClose: PropTypes.func,
	onAfterClose: PropTypes.func,
	title: PropTypes.string,
	autoClose: PropTypes.number,
	background: PropTypes.string,
	backButton: PropTypes.shape({
		title: PropTypes.string,
		onClick: PropTypes.func
	}),
	children: PropTypes.node,
	modifierClass: PropTypes.oneOf(['custom', 'minimalism']),
	size: PropTypes.oneOf(['medium']),
	theme: PropTypes.oneOf(['default', 'notify', 'transparent', 'compact']),
	shouldCloseOnOverlayClick: PropTypes.bool
};

Modal.defaultProps = {
	modifiers: ['size-l'],
	isOpen: true,
	autoClose: 0,
	modifierClass: 'custom',
	theme: 'default',
	shouldCloseOnOverlayClick: true
};

export default Modal;
